import React, { useEffect } from 'react';
import { Flex } from 'glints-aries/es';
import { Card, Icon, OutlineButton, Typography } from 'glints-aries/es/@next';
import { Green, Neutral } from 'glints-aries/es/@next/utilities/colors';
import {
  space4,
  space12,
  space24,
} from 'glints-aries/es/@next/utilities/spacing';
import { useController, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { WhatsAppGreen } from '../../../common/color';
import { ASSET_TYPES } from '../../../common/constants';
import { BusinessVerificationLocationType } from '../../../common/interfaces/entities';
import { User } from '../../../models/User';
import { getUser } from '../../../selectors/user';
import { Variant } from '../WhatsappVerificationModal/components/VerificationSuccessStep/constants';
import { useWhatsappVerificationModal } from '../WhatsappVerificationModal/useWhatsappVerificationModal';
import FileUploader from './components/FileUploader';
import MultiFileUploader from './components/MultiFileUploader/MultiFileUploader';
import RadioGroup from './components/RadioGroup';
import TextField from './components/TextField';
import {
  businessLocationTypeToText,
  DefaultFileUploadHelperText,
  MIN_BUSINESS_ACTIVITY_IMAGES,
  personalDocumentTypeToText,
  proofOfOwnershipRequiredFieldsMessages,
} from './constants';
import { useCompanyVerificationFormContext } from './FormContext';
import { useCompanyVerificationTracking } from './hooks/useCompanyVerificationTracking';
import { FormContentStyles } from './styles.sc';
import { VerifyWithProofPersonalDocumentType } from './types';
import VerificationAdditionalDocumentsUploader from './VerificationAdditionalDocumentsUploader';
import VerificationPopover from './VerificationPopover';

function RenderPersonalDocumentFields() {
  const selectedDocumentType = useWatch({
    control: useCompanyVerificationFormContext().control,
    name: 'verifyWithProofOfOwnership.personalDocumentType',
  });
  const { formatMessage } = useIntl();
  const { trackCompanyVerificationClickEvent, trackOnFieldValid } =
    useCompanyVerificationTracking();

  const onPersonalDocumentTypeChange = (
    value: VerifyWithProofPersonalDocumentType
  ) => {
    const idType =
      value === VerifyWithProofPersonalDocumentType.KTP
        ? 'ktp_pribadi'
        : 'npwp_pribadi';

    trackCompanyVerificationClickEvent('verification_id_type_selected', {
      // eslint-disable-next-line camelcase
      id_type: idType,
    });
  };

  const idNumberFieldName =
    selectedDocumentType === VerifyWithProofPersonalDocumentType.KTP
      ? 'verifyWithProofOfOwnership.ktpNumber'
      : 'verifyWithProofOfOwnership.personalNPWPNumber';
  const idNumberOnBlur = trackOnFieldValid(
    idNumberFieldName,
    'verification_id_number_filled'
  );

  return (
    <>
      <Flex flexDirection="column" gap={space12}>
        <Flex flexDirection="column" gap={space4}>
          <RadioGroup
            fieldName="verifyWithProofOfOwnership.personalDocumentType"
            title={
              <FormattedMessage
                id="text-choose-1-option"
                defaultMessage="Choose 1 Option"
              />
            }
            isRequired={true}
            options={Object.values(VerifyWithProofPersonalDocumentType).map(
              documentType => {
                const messages = personalDocumentTypeToText[documentType];
                return {
                  label: <FormattedMessage {...messages.optionText} />,
                  tooltip: (
                    <VerificationPopover
                      title={<FormattedMessage {...messages.tooltip.title} />}
                      items={[
                        {
                          title: (
                            <FormattedMessage
                              id="text-requirements"
                              defaultMessage="Requirements"
                            />
                          ),
                          items: [
                            <FormattedMessage
                              key={`${documentType}.tooltip`}
                              {...messages.tooltip.description}
                            />,
                          ],
                        },
                      ]}
                      images={messages.tooltip.images.slice()}
                    />
                  ),
                  value: documentType,
                };
              }
            )}
            onValueChange={value => {
              onPersonalDocumentTypeChange(
                value as VerifyWithProofPersonalDocumentType
              );
            }}
          />
          <Flex alignItems="flex-start" gap={space4}>
            <Icon
              name="ri-verified-line"
              width={20}
              height={20}
              fill={Green.B52}
              style={{ flexShrink: 0 }}
            />
            <Typography variant="subtitle2" color={Green.B52}>
              <FormattedMessage
                id="text-npwp-image-upload-guidance"
                defaultMessage="If uploading an image, make sure it's clear, readable, and not a black-and-white photocopy to avoid resubmission."
              />
            </Typography>
          </Flex>
        </Flex>
        <FileUploader
          key={`${selectedDocumentType}.doc`}
          assetType={
            selectedDocumentType === VerifyWithProofPersonalDocumentType.NPWP
              ? ASSET_TYPES.PERSONAL_NPWP_DOCUMENT
              : ASSET_TYPES.KTP_DOCUMENT
          }
          fieldName={
            selectedDocumentType === VerifyWithProofPersonalDocumentType.NPWP
              ? 'verifyWithProofOfOwnership.personalNPWPDoc'
              : 'verifyWithProofOfOwnership.ktpDoc'
          }
          buttonText={
            <FormattedMessage
              id="interactive-select-file"
              defaultMessage="Select File"
            />
          }
          title={null}
          hideTitle={true}
          acceptedFileType={['application/pdf', 'image/jpeg', 'image/png']}
          helperText={DefaultFileUploadHelperText}
        />
      </Flex>

      <TextField
        fullWidth={true}
        fieldName={idNumberFieldName}
        key={`${selectedDocumentType}.docNumber`}
        title={
          <FormattedMessage
            {...personalDocumentTypeToText[selectedDocumentType].documentNumber
              .title}
          />
        }
        placeholder={formatMessage({
          ...personalDocumentTypeToText[selectedDocumentType].documentNumber
            .placeholder,
        })}
        isRequired={true}
        onBlur={idNumberOnBlur}
      />
    </>
  );
}

export default function VerifyWithProofOfOwnershipForm({
  VerificationBanner,
}: {
  VerificationBanner: React.ReactNode;
}) {
  const { formatMessage } = useIntl();

  const { trackOnFieldValid, trackCompanyVerificationClickEvent } =
    useCompanyVerificationTracking();
  const personalNIBNumberFieldName =
    'verifyWithProofOfOwnership.personalNIBNumber';
  const personalNIBNumberOnBlur = trackOnFieldValid(
    personalNIBNumberFieldName,
    'verification_personal_nib_number_filled'
  );

  const onBusinessVerificationLocationTypeChange = (
    value: BusinessVerificationLocationType
  ) => {
    const activeType =
      value === BusinessVerificationLocationType.ONLINE ? 'online' : 'offline';
    trackCompanyVerificationClickEvent('verification_activity_type_selected', {
      // eslint-disable-next-line camelcase
      activity_type: activeType,
    });
  };

  return (
    <FormContentStyles.Container>
      <FormContentStyles.FullWidthCardContainer>
        <Card>
          <Card.Section>
            <Flex flexDirection="column" gap={space24}>
              {VerificationBanner}
              <WhatsAppVerificationStatus />
              <RenderPersonalDocumentFields />
              <TextField
                fullWidth={true}
                fieldName={personalNIBNumberFieldName}
                title={
                  <FormattedMessage
                    id="text-personal-nib"
                    defaultMessage="Personal NIB"
                  />
                }
                placeholder={formatMessage({
                  id: 'text-placeholder-enter-nib',
                  defaultMessage: 'Enter 13 Digits NIB',
                })}
                helperText={
                  <FormattedMessage
                    id="text-providing-your-nib-speeds"
                    defaultMessage="Providing your NIB speeds up verification"
                  />
                }
                onBlur={personalNIBNumberOnBlur}
              />
              <RadioGroup
                fieldName="verifyWithProofOfOwnership.businessLocationType"
                direction="column"
                title={
                  <FormattedMessage
                    id="text-choose-1-option"
                    defaultMessage="Choose 1 Option"
                  />
                }
                isRequired={true}
                options={Object.values(BusinessVerificationLocationType).map(
                  locationType => ({
                    label: (
                      <FormattedMessage
                        {...businessLocationTypeToText[locationType].optionText}
                        values={{
                          count: MIN_BUSINESS_ACTIVITY_IMAGES,
                        }}
                      />
                    ),
                    tooltip: (
                      <VerificationPopover
                        title={
                          <FormattedMessage
                            {...businessLocationTypeToText[locationType].tooltip
                              .title}
                          />
                        }
                        items={[
                          {
                            title: (
                              <FormattedMessage
                                id="text-requirements"
                                defaultMessage="Requirements"
                              />
                            ),
                            items: [
                              <FormattedMessage
                                key={`${locationType}.tooltip`}
                                {...businessLocationTypeToText[locationType]
                                  .tooltip.description}
                              />,
                            ],
                          },
                        ]}
                        imagesDirection={
                          businessLocationTypeToText[locationType].tooltip
                            .imagesDirection
                        }
                        images={businessLocationTypeToText[
                          locationType
                        ].tooltip.images.slice()}
                      />
                    ),
                    value: locationType,
                  })
                )}
                onValueChange={value => {
                  onBusinessVerificationLocationTypeChange(
                    value as BusinessVerificationLocationType
                  );
                }}
              />
              <MultiFileUploader
                title={null}
                hideTitle={true}
                assetType={ASSET_TYPES.BUSINESS_IMAGE}
                fieldName="verifyWithProofOfOwnership.businessActivityImages"
                maxImages={5}
              />
              <VerificationAdditionalDocumentsUploader fieldName="verifyWithProofOfOwnership.additionalDocuments" />
            </Flex>
          </Card.Section>
        </Card>
      </FormContentStyles.FullWidthCardContainer>
      <FormContentStyles.RequiredSectionContainer>
        <Card>
          <Card.Section>
            <Flex flexDirection="column" gap={space12}>
              <Typography variant="body2">
                <FormattedMessage
                  id="text-required"
                  defaultMessage="Required"
                />
              </Typography>
              <Typography variant="body1" color={Neutral.B40} as="div">
                {proofOfOwnershipRequiredFieldsMessages.map(
                  (message, index) => (
                    <FormContentStyles.TypographyWithBulletPoint key={index}>
                      <FormattedMessage {...message} />
                    </FormContentStyles.TypographyWithBulletPoint>
                  )
                )}
              </Typography>
            </Flex>
          </Card.Section>
        </Card>
      </FormContentStyles.RequiredSectionContainer>
    </FormContentStyles.Container>
  );
}

function WhatsAppVerificationStatus() {
  const { control } = useCompanyVerificationFormContext();
  const user: User = useSelector(getUser);
  const { openModal, whatsappVerificationModal } =
    useWhatsappVerificationModal();
  const {
    field: {
      value: isWhatsappVerified,
      onChange: setWhatsappVerificationStatus,
    },
  } = useController({
    name: 'verifyWithProofOfOwnership.isWhatsAppVerified',
    control,
    defaultValue: Boolean(user.isWhatsappVerified),
  });

  useEffect(() => {
    setWhatsappVerificationStatus(user.isWhatsappVerified);
  }, [setWhatsappVerificationStatus, user.isWhatsappVerified]);

  return (
    <>
      {whatsappVerificationModal}
      <Flex flexDirection="column" gap={space12}>
        <Typography variant="body2">
          <FormattedMessage
            id="text-whatsapp-verification"
            defaultMessage="WhatsApp Verification"
            tagName="span"
          />
          <FormContentStyles.Asterisk />
        </Typography>
        <FormContentStyles.WhatsAppVerificationStatusContainer>
          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Typography variant="caption">
                <FormattedMessage
                  id="text-verification-status"
                  defaultMessage="Verification Status"
                />
              </Typography>
              <Flex gap={space4}>
                <Icon
                  name="ri-checkbox-circle-fill"
                  fill={isWhatsappVerified ? Green.B61 : Neutral.B85}
                  width={20}
                  height={20}
                />
                <Typography variant="subtitle2">
                  <Choose>
                    <When condition={isWhatsappVerified}>
                      <FormattedMessage
                        id="text-verified"
                        defaultMessage="Verified"
                      />
                    </When>
                    <Otherwise>
                      <FormattedMessage
                        id="text-unverified"
                        defaultMessage="Unverified"
                      />
                    </Otherwise>
                  </Choose>
                </Typography>
              </Flex>
            </div>
            <If condition={!isWhatsappVerified}>
              <OutlineButton
                onClick={() =>
                  openModal({
                    successStepVariant: Variant.NOTIFICATION,
                    successButtonText: {
                      id: 'interactive-continue-with-verification',
                      defaultMessage: 'Continue with Verification',
                    },
                  })
                }
                icon={
                  <Icon
                    name="ri-whatsapp-fill"
                    width={20}
                    fill={WhatsAppGreen}
                  />
                }
              >
                <Typography variant="button">
                  <FormattedMessage
                    id="text-verify-whatsapp"
                    defaultMessage="Verify WhatsApp"
                  />
                </Typography>
              </OutlineButton>
            </If>
          </Flex>
        </FormContentStyles.WhatsAppVerificationStatusContainer>
      </Flex>
    </>
  );
}
